/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import { ConditionDetailsForBoxes } from 'cio/utils/condition-types';
import ConditionList, { DropdownOption } from 'cio/components/condition-list';
import { findOperator } from 'cio/utils/conditions-operators';
import { SerializedLegacyAttributeFilter } from 'cio/types/conditions/legacy-filter';

export default class Boxes extends ConditionList {
  @tracked showAllBoxes = this.markedConditions?.length === 0;
  @tracked isEditingIndex: number | null = null;

  readonly ConditionDetailsForBoxes = ConditionDetailsForBoxes;

  get showMarkedConditions() {
    if (this.args.addStyle === this.AddStyleVariants.boxes) {
      if (this.isAdding || this.showAllBoxes) {
        return this.args.allowChangeTypeInline === true && this.markedConditions;
      }
    }
    return this.markedConditions;
  }

  get showAnyMarkedConditionOnEdit() {
    if (this.isEditingIndex !== null) {
      return this.args.allowChangeTypeInline === true;
    }
    return this.showMarkedConditions;
  }

  get showBackButton() {
    let conditionsCount = (this.args.conditions as any)?.length ?? 0;
    if (this.showAllBoxes && conditionsCount > 0) {
      return true;
    }
    if (this.isAdding) {
      // if there are no conditions set and we have "startInCreateState" arg set to true, then do not offer back button
      if (conditionsCount === 0 && this.args.startInCreateState === true) {
        return false;
      }
      return this.args.allowChangeTypeInline !== true;
    }
    return false;
  }

  get parsedConditions() {
    let { conditions, useUpdateLabel } = this.args;
    return (
      (conditions as SerializedLegacyAttributeFilter[])
        ?.map((condition) => {
          let operator = findOperator(condition.operator);
          if (operator == null) {
            return;
          }
          let sentence = `${condition.field} ${operator[useUpdateLabel === true ? 'updateLabel' : 'label']}`;
          return {
            sentence,
            condition,
          };
        })
        .filter((value) => value != null) ?? []
    );
  }

  get changeTypeOptions() {
    return (this.typeButtons ?? []).map(
      (typeButton) =>
        <DropdownOption>{
          label: typeButton.addButtonLabel,
          value: typeButton.name,
          disabled: Boolean(typeButton.isDisabled),
        },
    );
  }

  get editContainerClass() {
    let classnames = this.args.editContainerClass ?? '';
    return `${classnames} w-full`;
  }

  get addingConditionLabel() {
    let label = this.newConditionLabel;
    if (this.args.preferFilterOverCondition === true) {
      label = label.replace('condition', 'filter');
    }
    return label;
  }

  @action
  onAddCondition() {
    let { allowChangeTypeInline, initialCreateConditionType } = this.args;
    let { typeButtons = [] } = this;
    if (allowChangeTypeInline === true && initialCreateConditionType != null) {
      this.add(initialCreateConditionType);
      return;
    }
    if (typeButtons.length === 1) {
      this.add(typeButtons[0].name);
    }
    this.showAllBoxes = true;
  }

  @action
  create(...args: Parameters<ConditionList['create']>) {
    super.create(...args);
    this.showAllBoxes = false;
  }

  @action
  delete(index: number) {
    let { deserializedFilters: conditions } = this;
    let newConditions = [...conditions.slice(0, index), ...conditions.slice(index + 1)];
    this.showAllBoxes = this.args.allowChangeTypeInline !== true && newConditions.length === 0;
    this.args.onUpdate(serializeFilters(newConditions, this.operator, this.isFilters));
    if (this.args.allowChangeTypeInline === true && newConditions.length === 0) {
      this.onAddCondition();
    }
  }

  @action
  update(...args: Parameters<ConditionList['update']>) {
    this.isEditingIndex = null;
    super.update(...args);
  }

  @action
  add(...args: Parameters<ConditionList['add']>) {
    super.add(...args);
    this.showAllBoxes = false;
  }

  @action
  cancel(...args: Parameters<ConditionList['cancel']>) {
    super.cancel(...args);
    this.showAllBoxes = this.markedConditions?.length === 0;
  }

  @action
  didCreateSegment(...args: Parameters<ConditionList['didCreateSegment']>) {
    super.didCreateSegment(...args);
    this.showAllBoxes = false;
  }

  @action
  setIsEditingAttribute(index: number, isEditing: boolean) {
    this.isEditingIndex = isEditing ? index : null;
  }
}
